import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { getStatusBadgeStyle, formatDate } from 'utils/lmmsHelpers';
import { SyutSelect } from 'components/ui';

// ============================================================================
// Types
// ============================================================================

export interface EntityFormData {
  entityCode: string;
  entityName: string;
  entityType: string;
  description: string;
}

export interface EntityRecord {
  id: string;
  entityCode: string;
  entityName: string;
  entityType: string;
  parentEntityId?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  updatedAt?: string;
  updatedByName?: string;
  rowVersion: number;
}

interface EntityFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: EntityFormData;
  onFormDataChange: (data: EntityFormData) => void;
  onSave: () => void;
  onClose: () => void;
}

interface EntityViewModalProps {
  isOpen: boolean;
  record: EntityRecord | null;
  onClose: () => void;
}

const ENTITY_TYPE_OPTIONS = [
  { value: 'COMPANY', label: 'Company' },
  { value: 'HOLDING_COMPANY', label: 'Holding Company' },
];

// ============================================================================
// Form Modal (Create / Edit)
// ============================================================================

export const EntityFormModal: React.FC<EntityFormModalProps> = ({
  isOpen,
  mode,
  formData,
  onFormDataChange,
  onSave,
  onClose,
}) => {
  if (!isOpen) return null;

  const title = mode === 'create' ? 'Add New Entity' : 'Edit Entity';

  const handleChange = (field: keyof EntityFormData, value: string) => {
    onFormDataChange({ ...formData, [field]: value });
  };

  const isValid = formData.entityCode.trim() !== '' && formData.entityName.trim() !== '';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-group">
            <label className="form-label">Entity Code *</label>
            <input
              type="text"
              className="form-input"
              value={formData.entityCode}
              onChange={(e) => handleChange('entityCode', e.target.value)}
              placeholder="e.g., VCL"
              maxLength={20}
              style={{ border: '1px solid #334155' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Entity Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.entityName}
              onChange={(e) => handleChange('entityName', e.target.value)}
              placeholder="e.g., VCL Finance Ltd"
              maxLength={100}
              style={{ border: '1px solid #334155' }}
            />
          </div>

          <div className="form-group">
            <SyutSelect
              label="Entity Type *"
              options={ENTITY_TYPE_OPTIONS}
              value={formData.entityType}
              onChange={(val: string | string[] | null) => handleChange('entityType', (val as string) || 'COMPANY')}
              placeholder="Select type..."
              isClearable={false}
              size="sm"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Optional description"
              rows={3}
              maxLength={500}
              style={{ border: '1px solid #334155', resize: 'vertical' }}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onSave} disabled={!isValid}>
            <IconSystem.save size={16} />
            {mode === 'create' ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// View Modal
// ============================================================================

export const EntityViewModal: React.FC<EntityViewModalProps> = ({
  isOpen,
  record,
  onClose,
}) => {
  if (!isOpen || !record) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h2 className="modal-title">Entity Details</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div>
              <label className="form-label">Entity Code</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.entityCode}
              </div>
            </div>
            <div>
              <label className="form-label">Entity Name</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.entityName}
              </div>
            </div>
            <div>
              <label className="form-label">Entity Type</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.entityType}
              </div>
            </div>
            <div>
              <label className="form-label">Status</label>
              <div style={{ padding: '0.5rem' }}>
                <span style={getStatusBadgeStyle(record.isActive ? 'ACTIVE' : 'INACTIVE')}>
                  {record.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            {record.description && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Description</label>
                <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                  {record.description}
                </div>
              </div>
            )}
            <div>
              <label className="form-label">Created By</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.createdByName || '—'}
              </div>
            </div>
            <div>
              <label className="form-label">Created At</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {formatDate(record.createdAt)}
              </div>
            </div>
            {record.updatedAt && (
              <>
                <div>
                  <label className="form-label">Updated By</label>
                  <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                    {record.updatedByName || '—'}
                  </div>
                </div>
                <div>
                  <label className="form-label">Updated At</label>
                  <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                    {formatDate(record.updatedAt)}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};
